import IdentificationTypesSelect from '@/components/common/input/identification-types-select/IdentificationTypesSelect';
import CalendarModal from '@/components/modals/calendar-modal/CalendarModal';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import type { TUserData } from '@/services/api/user-api';
import type { StepService } from '@/stores/slices/step-service/step-services.types';
import {
  maxHeightInputValidation,
  maxWeightInputValidation
} from '@/utils/utils';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const UserForm: React.FC<{
  form: UseFormReturn<any, any, undefined>;
  userIsParticipant: boolean;
  user: TUserData | undefined;
}> = ({ form, userIsParticipant, user }) => {
  const stepServicesData: StepService | null = useRootStore(
    (store) => store.stepServicesData
  );
  const { t } = useTranslation(['newBooking', 'common']);
  const Mixpanel = useMixPanel();

  const renderConditionalInputs = () =>
    userIsParticipant ? (
      <>
        <FormField
          control={form.control}
          name={`user.birthdate`}
          render={({ field }) => {
            const disabled = !!user?.birthdate;
            return (
              <FormItem className="flex flex-col ">
                <FormLabel>{t('stepParticipants.form.birth')}</FormLabel>
                <CalendarModal
                  value={field.value}
                  placeholder={t('stepParticipants.form.birth')}
                  onAccept={(value) => {
                    field.onChange(value);
                    Mixpanel.track('select date from birthday calendar ', {
                      screen: 'New Booking'
                    });
                  }}
                  disabledInputLabel={disabled}
                  onClickInputLabel={() =>
                    Mixpanel.track('press birthday date button ', {
                      screen: 'New Booking'
                    })
                  }
                  initialFocus
                  dropDownPicker
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name={`user.weight`}
          render={({ field }) => {
            const disabled = !!user?.weight;
            return (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.weight')}</FormLabel>
                <FormControl>
                  <Input
                    onClick={() =>
                      Mixpanel.track('press user weight input', {
                        screen: 'New Booking'
                      })
                    }
                    type="number"
                    {...field}
                    readOnly={disabled}
                    onChange={(e) => {
                      if (maxWeightInputValidation(Number(e.target.value))) {
                        field.onChange(e);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name={`user.height`}
          render={({ field }) => {
            const disabled = !!user?.height;
            return (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.height')}</FormLabel>
                <FormControl>
                  <Input
                    onClick={() =>
                      Mixpanel.track('press user height input', {
                        screen: 'New Booking'
                      })
                    }
                    step="1"
                    type="number"
                    {...field}
                    readOnly={disabled}
                    onChange={(e) => {
                      if (maxHeightInputValidation(Number(e.target.value))) {
                        field.onChange(e);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </>
    ) : (
      <>
        <FormField
          control={form.control}
          key={'user.identification.type'}
          name="user.identification.type"
          render={({ field }) => {
            const disabled = !!user?.identification?.type;
            return (
              <FormItem>
                <IdentificationTypesSelect
                  disabled={disabled}
                  defaultValue={field?.value}
                  onValueChange={(value) => {
                    Mixpanel.track('press user id input', {
                      screen: 'New Booking'
                    });
                    field.onChange(value);
                  }}
                  field={field}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="user.identification.number"
          key="user.identification.number"
          render={({ field }) => {
            const disabled = !!user?.identification?.number;
            return (
              <FormItem>
                <FormLabel>
                  {t('stepParticipants.form.identificationNumber')}
                </FormLabel>
                <FormControl>
                  <Input
                    onClick={() =>
                      Mixpanel.track('press participants id number input', {
                        screen: 'New Booking'
                      })
                    }
                    readOnly={disabled}
                    placeholder={t('stepParticipants.form.id')}
                    {...field}
                    value={field?.value || ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </>
    );

  return (
    <div
      className="border border-primary p-6 rounded-md flex flex-col gap-8"
      key={`user-participant`}
    >
      <h3 className="text-sm font-normal text-primary">
        {t('stepParticipants.yourData')}
        {userIsParticipant
          ? ` - ${t('stepParticipants.participant')} 1 - ${
              stepServicesData?.service?.name
            }`
          : null}
      </h3>
      <FormField
        control={form.control}
        name={`user.firstName`}
        render={({ field }) => {
          const disabled = !!user?.firstName;
          return (
            <FormItem>
              <FormLabel>{t('stepParticipants.form.firstName')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onClick={() =>
                    Mixpanel.track('press user name input', {
                      screen: 'New Booking'
                    })
                  }
                  readOnly={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name={`user.lastName`}
        render={({ field }) => {
          const disabled = !!user?.firstName;
          return (
            <FormItem>
              <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
              <FormControl>
                <Input
                  onClick={() =>
                    Mixpanel.track('press lastName input', {
                      screen: 'New Booking'
                    })
                  }
                  {...field}
                  readOnly={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        key={'user.phone'}
        control={form.control}
        name={`user.phone`}
        render={({ field }) => {
          const disabled = !!user?.phone;
          return (
            <FormItem>
              <FormLabel>{t('phone')}</FormLabel>
              <FormControl>
                <Input
                  onClick={() =>
                    Mixpanel.track('press user phone input', {
                      screen: 'New Booking'
                    })
                  }
                  type="tel"
                  {...field}
                  readOnly={disabled}
                  placeholder={t('common:placeholder.phone')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      {renderConditionalInputs()}
    </div>
  );
};

export default UserForm;
